import { z } from 'zod'
import { IsoDateTimeSchema } from '../primitives'

/* Requirements (membership rules) moved to domain/requirements.ts (PLAN v4: RequirementsV2). */

/* ------------------------------------------------------------------ residence declaration (per voting) */

/** Default "resided at least N days" declaration asked on the ballot (client requirement: 180 days). */
export const DEFAULT_RESIDENCE_DECLARATION_DAYS = 180
/** Residence declaration checkbox at voting time ("resided N days"), null = not asked. */
export const ResidenceDeclarationDaysSchema = z.number().int().min(1).max(3650).nullable()

/* ------------------------------------------------------------------ schedule */

export const START_MODES = ['scheduled', 'manual'] as const
export const END_MODES = ['duration', 'scheduled', 'manual'] as const
export const StartModeSchema = z.enum(START_MODES)
export const EndModeSchema = z.enum(END_MODES)

/** Minimum lead time for a scheduled start (processes are created at publish with startDate ≥ now+10min). */
export const MIN_SCHEDULED_START_LEAD_SECONDS = 10 * 60
/** Process duration used when the end is on-demand ("End now" closes early). */
export const MANUAL_END_DURATION_SECONDS = 30 * 24 * 3600
export const MIN_DURATION_SECONDS = 15 * 60
export const MAX_DURATION_SECONDS = 90 * 24 * 3600

export const ScheduleSchema = z
  .object({
    startMode: StartModeSchema.default('manual'),
    /** Required when startMode = scheduled. */
    startAt: IsoDateTimeSchema.nullable().default(null),
    endMode: EndModeSchema.default('manual'),
    /** Required when endMode = scheduled. */
    endAt: IsoDateTimeSchema.nullable().default(null),
    /** Required when endMode = duration. */
    durationSeconds: z.number().int().min(MIN_DURATION_SECONDS).max(MAX_DURATION_SECONDS).nullable().default(null),
  })
  .superRefine((s, ctx) => {
    if (s.startMode === 'scheduled' && !s.startAt)
      ctx.addIssue({ code: 'custom', path: ['startAt'], message: 'Start date is required' })
    if (s.endMode === 'scheduled' && !s.endAt)
      ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'End date is required' })
    if (s.endMode === 'duration' && !s.durationSeconds)
      ctx.addIssue({ code: 'custom', path: ['durationSeconds'], message: 'Duration is required' })
    if (s.startMode === 'scheduled' && s.endMode === 'scheduled' && s.startAt && s.endAt) {
      if (Date.parse(s.endAt) - Date.parse(s.startAt) < MIN_DURATION_SECONDS * 1000)
        ctx.addIssue({ code: 'custom', path: ['endAt'], message: 'End must be at least 15 minutes after start' })
    }
  })
export type Schedule = z.infer<typeof ScheduleSchema>
export type ScheduleDraft = z.input<typeof ScheduleSchema>

export const defaultSchedule = (): Schedule => ({
  startMode: 'manual',
  startAt: null,
  endMode: 'manual',
  endAt: null,
  durationSeconds: null,
})

/* ------------------------------------------------------------------ districts */

export const DistrictEntrySchema = z.object({
  locality: z.string().trim().min(1).max(200),
  district: z.string().trim().min(1).max(200),
})
export type DistrictEntry = z.infer<typeof DistrictEntrySchema>

/**
 * Organization districting list (CSV `locality,district`). Empty = no districting (locality is free text).
 * Votings may restrict eligibility to some of its districts (`ElectionBody.eligibleDistricts`).
 */
export const DistrictsSchema = z.array(DistrictEntrySchema).max(20_000).default([])
export type Districts = z.infer<typeof DistrictsSchema>

/** Distinct district names of a districting list, sorted (eligible-district pickers, filters, map legend). */
export const districtNames = (districts: readonly DistrictEntry[]): string[] =>
  [...new Set(districts.map((d) => d.district))].sort((a, b) => a.localeCompare(b))
